/**
 * Budget Export Modal
 * 
 * Modal for selecting export options and format
 */

import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import type { BudgetData } from '@/types/budget.types';
import { exportBudget, type ExportOptions } from '@/lib/budget-export';

interface BudgetExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgetData: BudgetData;
  /** When true, only shows resource budget export options (no cost/sales/summary, no PDF, Excel only) */
  resourceOnly?: boolean;
}

export function BudgetExportModal({
  open,
  onOpenChange,
  budgetData,
  resourceOnly = false,
}: BudgetExportModalProps) {
  const [format, setFormat] = useState<'excel' | 'pdf'>('excel');
  const [options, setOptions] = useState<ExportOptions>({
    includeCharts: true,
    includeSummary: !resourceOnly, // Disabled for resource-only mode
    includeCost: !resourceOnly, // Disabled for resource-only mode
    includeSales: !resourceOnly, // Disabled for resource-only mode
    includeResource: true,
    includeHistory: false,
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportBudget(budgetData, format, options);
      onOpenChange(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export budget. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleOption = (key: keyof ExportOptions) => {
    setOptions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Budget
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Format Selection */}
          {!resourceOnly && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Export Format</Label>
              <RadioGroup value={format} onValueChange={(value) => setFormat(value as 'excel' | 'pdf')}>
                <div className="flex items-center space-x-2 rounded-lg border p-4 hover:bg-accent">
                  <RadioGroupItem value="excel" id="excel" />
                  <Label htmlFor="excel" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="h-5 w-5 text-success-600" />
                      <div>
                        <div className="font-medium">Excel (.xlsx)</div>
                        <div className="text-sm text-muted-foreground">
                          Spreadsheet format with multiple sheets, perfect for analysis
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>

                <div className="flex items-center space-x-2 rounded-lg border p-4 hover:bg-accent">
                  <RadioGroupItem value="pdf" id="pdf" />
                  <Label htmlFor="pdf" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-error-600" />
                      <div>
                        <div className="font-medium">PDF (.txt)</div>
                        <div className="text-sm text-muted-foreground">
                          Formatted text report, perfect for sharing and printing
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {!resourceOnly && <Separator />}

          {/* Content Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Include in Export</Label>
            <div className="space-y-3">
              {!resourceOnly && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="summary"
                      checked={options.includeSummary}
                      onCheckedChange={() => toggleOption('includeSummary')}
                    />
                    <Label htmlFor="summary" className="cursor-pointer font-normal">
                      Budget Summary (Revenue, Costs, Margin, Hours, Health)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cost"
                      checked={options.includeCost}
                      onCheckedChange={() => toggleOption('includeCost')}
                    />
                    <Label htmlFor="cost" className="cursor-pointer font-normal">
                      Cost Budget (Categories, Phases, Breakdown)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sales"
                      checked={options.includeSales}
                      onCheckedChange={() => toggleOption('includeSales')}
                    />
                    <Label htmlFor="sales" className="cursor-pointer font-normal">
                      Sales Budget (Revenue, Milestones, Rate Card)
                    </Label>
                  </div>
                </>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resource"
                  checked={options.includeResource}
                  onCheckedChange={() => toggleOption('includeResource')}
                  disabled={resourceOnly}
                />
                <Label htmlFor="resource" className="cursor-pointer font-normal">
                  Resource Budget (by Role, Person, Phase)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="history"
                  checked={options.includeHistory}
                  onCheckedChange={() => toggleOption('includeHistory')}
                />
                <Label htmlFor="history" className="cursor-pointer font-normal">
                  Revision History (All budget changes)
                </Label>
              </div>

              {format === 'excel' && !resourceOnly && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="charts"
                    checked={options.includeCharts}
                    onCheckedChange={() => toggleOption('includeCharts')}
                    disabled
                  />
                  <Label htmlFor="charts" className="cursor-pointer font-normal text-muted-foreground">
                    Include Charts (Coming soon)
                  </Label>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Export Info */}
          <div className="rounded-lg bg-muted p-4 text-sm">
            <div className="font-medium mb-2">Export Details</div>
            <div className="space-y-1 text-muted-foreground">
              <div>Project: {budgetData.projectName}</div>
              <div>Date: {new Date().toLocaleDateString()}</div>
              <div>
                Format: {resourceOnly ? 'Excel (.xlsx)' : format === 'excel' ? 'Excel-compatible CSV' : 'Formatted Text Report'}
              </div>
              {resourceOnly && <div>Content: Resource Budget Only</div>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting}
              className="gap-2"
            >
              {isExporting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  {resourceOnly ? 'Export to Excel' : `Export ${format === 'excel' ? 'to Excel' : 'to PDF'}`}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
